<template>
  <article>
    <activity-stage :customRootContainerClass="'homonym'">
      <template slot="instructions">
        <slot name="instructions"></slot>
      </template>
      <article slot="content">
        <div
          class="steps-container"
          v-if="currentRound.steps"
          transition="fade"
          style="display: flex"
        >
          <div class="step" transition="fade">
            <img alt="Left image" v-bind:src="images.left" />
            <text-display-list text-size="small">
              <li transition="fade">
                <blinking-cursor-text
                  :text="stepLeft.answer"
                  :text-step="0"
                  :shouldBeCurrentStep="progress.stepIndex === 0 && textToType === 'answer'"
                  :shouldBeCompletedStep="progress.stepIndex > 0 || textToType === 'description'"
                >
                </blinking-cursor-text>
              </li>
              <li transition="fade">
                <blinking-cursor-text
                  :text="stepLeft.description"
                  :text-step="0"
                  :shouldBeCurrentStep="progress.stepIndex === 0 && textToType === 'description'"
                  :shouldBeCompletedStep="progress.stepIndex > 0"
                >
                </blinking-cursor-text>
              </li>
            </text-display-list>
          </div>
          <div class="step" v-show="showRight" transition="fade">
            <img alt="Right image" v-bind:src="images.right" />
            <text-display-list text-size="small">
              <li v-show="progress.stepIndex >= 1" transition="fade">
                <blinking-cursor-text
                  :text="stepRight.answer"
                  :text-step="1"
                  :shouldBeCurrentStep="progress.stepIndex === 1 && textToType === 'answer'"
                  :shouldBeCompletedStep="textToType === 'description'"
                >
                  >
                </blinking-cursor-text>
              </li>
              <li v-show="progress.stepIndex >= 1" transition="fade">
                <blinking-cursor-text
                  :text="stepRight.description"
                  :text-step="1"
                  :shouldBeCurrentStep="progress.stepIndex === 1 && textToType === 'description'"
                >
                </blinking-cursor-text>
              </li>
            </text-display-list>
          </div>
        </div>
      </article>
    </activity-stage>
    <slot name="keyboard"></slot>
  </article>
</template>

<script>
import { TweenMax } from 'gsap';

import AssetLoader from 'mixins/AssetLoader';
import ErrorLogger from 'mixins/ErrorLogger';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import ActivityManager from 'mixins/ActivityManager';

import ActivityStage from 'components/activity/ActivityStage.vue';
import BlinkingCursorText from 'components/text-displays/BlinkingCursorText.vue';
import TextDisplayList from 'components/text-displays/TextDisplayList.vue';

export default {
  components: {
    ActivityStage,
    BlinkingCursorText,
    TextDisplayList
  },

  mixins: [AssetLoader, TypingActivityHelper, ActivityManager, ErrorLogger],

  data() {
    return {
      activityName: 'Homonym',
      textToType: 'answer'
    };
  },
  beforeDestroy() {
    TweenMax.killAll();
  },

  computed: {
    images() {
      const left = this.currentRound.steps[0].answerImage?.image?.src;
      const right = this.currentRound.steps[1].answerImage?.image?.src;

      return { left, right };
    },
    stepLeft() {
      return this.currentRound.steps?.[0];
    },
    stepRight() {
      return this.currentRound.steps?.[1];
    },
    showRight() {
      return this.progress.stepIndex === 1;
    },
    typingText() {
      return this.currentStep[this.textToType];
    }
  },

  methods: {
    async loadActivity() {
      const roundSetup = this.getActivityState();
      this.createRounds(roundSetup);
      await this.loadImages();
    },

    loadImages() {
      this.rounds[0].steps.forEach(step => this.queueImage(step.image));
      return this.downloadImages();
    },

    getActivityState() {
      const { rounds } = this.activityData;
      return {
        rounds,
        instructions: {
          written: 'Type each word and description.',
          voice: `${this.voiceInstructionsPrefix}/soundthesame-${this.getRandomVoice()}-1.mp3`
        }
      };
    },

    startActivity() {
      this.setupNewRound({ stepCallback: this.stepCallback });
      this.enableKeyboard();
      this.errorLoggerStart();

      this.$nextTick(this.setActivityContainerHeight);

      this.$nextTick(() => {
        TweenMax.to('.homonym', 0.5, { opacity: 1 });
      });
    },

    stepCallback(rootGetters) {
      return rootGetters['activity/currentStep'][this.textToType];
    },

    handleCorrectKey(attempt) {
      this.sndType();
      this.incrementTypingIndex();

      this.captureKeyboardAttemptSegment(attempt, this.isEndOfCurrentSentence);

      if (this.isEndOfCurrentSentence) {
        if (this.textToType === 'description') {
          this.finishStep();
        } else {
          this.sndBell();
          this.textToType = 'description';
          this.overwriteProgressText({ currentText: this.currentStep.description });
        }
      }
    },

    finishStep() {
      this.sndBell();
      this.completeStep();
      if (this.textToType === 'description' && this.progress.stepIndex === 1) {
        this.handleRoundEnd();
        return;
      }
      this.setCurrentStep({ stepCallback: this.stepCallback });
      this.textToType = 'answer';
      this.overwriteProgressText({ currentText: this.currentStep.answer });
    },

    handleRoundEnd() {
      if (this.isEndOfActivity) {
        TweenMax.delayedCall(1, this.completeActivity);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import 'src/assets/sass/fade.scss';

ul {
  list-style: none;
  margin-top: auto;
}

.keyboard-wrapper {
  position: absolute;
}

.steps-container {
  .step {
    display: inline-block;
    width: 50%;

    img {
      margin-left: 40px;
      width: 70%;
    }

    .type-line {
      font-size: 21px;

      &.title {
        margin-bottom: 5px;
      }
    }
  }
}

.is-chrome-ipad {
  .step {
    img {
      max-height: 200px;
    }
  }
}
</style>
